import { CheckCheck, CloudOff, GitMerge, RefreshCw, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useSyncOutbox, useSyncStatus } from "../../hooks/useSyncEngine";
import { extractErrorMessage } from "../../lib/api";
import { syncEngine } from "../../lib/sync/syncEngine";

const STATE_LABELS = {
  live: "Live — synced continuously",
  syncing: "Syncing with the cloud…",
  bootstrapping: "Downloading data to this device…",
  offline: "Offline — working from local data",
  error: "Sync error — retrying",
  idle: "Sync paused",
};

const formatClock = (isoValue) => (isoValue ? new Date(isoValue).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const formatStamp = (isoValue) => (isoValue ? new Date(isoValue).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const InfoRow = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className="font-medium text-zinc-900" data-testid={testId}>{value}</span>
  </div>
);

const OutboxItem = ({ item }) => (
  <li className="rounded-xl border border-zinc-200 bg-white px-3 py-2" data-testid={`sync-outbox-item-${item.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-zinc-900">{item.label || `${item.method.toUpperCase()} ${item.url}`}</p>
        <p className="text-xs text-zinc-500">{formatClock(item.createdAt)}{item.failed ? " · needs attention" : " · waiting to send"}</p>
        {item.error ? <p className="mt-1 text-xs text-red-600" data-testid={`sync-outbox-error-${item.id}`}>{item.error}</p> : null}
      </div>
      <div className="flex shrink-0 gap-1">
        {item.failed ? (
          <button className="rounded-full border border-zinc-200 p-1.5 text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid={`sync-outbox-retry-${item.id}`} onClick={() => syncEngine.retryOutboxItem(item.id)} title="Retry" type="button"><RotateCcw className="h-3.5 w-3.5" /></button>
        ) : null}
        <button className="rounded-full border border-red-200 p-1.5 text-red-600 transition hover:bg-red-50" data-testid={`sync-outbox-discard-${item.id}`} onClick={() => syncEngine.discardOutboxItem(item.id)} title="Discard" type="button"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  </li>
);

const ConflictItem = ({ conflict, canResolve, onResolved }) => {
  const [busy, setBusy] = useState(false);
  const resolve = async () => {
    try {
      setBusy(true);
      await syncEngine.resolveConflict(conflict.id);
      onResolved(conflict.id);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to mark this conflict as reviewed."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2" data-testid={`sync-conflict-item-${conflict.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900" data-testid={`sync-conflict-label-${conflict.id}`}>Load {conflict.entity_label || "—"}</p>
          <p className="mt-0.5 text-xs text-zinc-700" data-testid={`sync-conflict-summary-${conflict.id}`}>{conflict.summary || "Two dispatchers saved this load at the same time; the changes were merged."}</p>
          <p className="mt-1 text-[11px] text-zinc-500">{conflict.saved_by || "Someone"}{conflict.other_user ? ` vs ${conflict.other_user}` : ""} · {formatStamp(conflict.created_at)}</p>
        </div>
        {canResolve && conflict.status === "open" ? (
          <button className="inline-flex shrink-0 items-center gap-1 rounded-full border border-zinc-300 bg-white px-2 py-1 text-[11px] font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:opacity-60" data-testid={`sync-conflict-resolve-${conflict.id}`} disabled={busy} onClick={resolve} type="button"><CheckCheck className="h-3 w-3" />Reviewed</button>
        ) : null}
      </div>
    </li>
  );
};

const ConflictsTab = ({ isAdmin }) => {
  const [conflicts, setConflicts] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    const load = () => syncEngine.fetchConflicts("open").then((items) => active && setConflicts(items)).catch((err) => active && setError(extractErrorMessage(err, "Unable to load conflicts.")));
    load();
    const unsubscribe = syncEngine.subscribe((event) => { if (event.type === "conflicts") load(); });
    return () => { active = false; unsubscribe(); };
  }, []);
  if (error) {
    return <p className="text-xs text-red-600" data-testid="sync-conflicts-error">{error}</p>;
  }
  if (conflicts === null) {
    return <p className="text-sm text-zinc-500" data-testid="sync-conflicts-loading">Loading conflicts…</p>;
  }
  if (!conflicts.length) {
    return (
      <div className="rounded-2xl border border-dashed border-zinc-300 px-3 py-6 text-center" data-testid="sync-conflicts-empty">
        <GitMerge className="mx-auto h-5 w-5 text-emerald-600" />
        <p className="mt-2 text-sm font-medium text-zinc-900">No open conflicts</p>
        <p className="mt-1 text-xs text-zinc-500">When two dispatchers save the same load at once, the merge shows up here.</p>
      </div>
    );
  }
  return (
    <ul className="max-h-72 space-y-2 overflow-y-auto" data-testid="sync-conflicts-list">
      {conflicts.map((conflict) => <ConflictItem canResolve={isAdmin} conflict={conflict} key={conflict.id} onResolved={(id) => setConflicts((current) => current.filter((item) => item.id !== id))} />)}
    </ul>
  );
};

const StatusTab = ({ status, outbox }) => (
  <>
    <div className="space-y-2 rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-3">
      <InfoRow label="Connection" testId="sync-center-connection" value={status.online ? "Online" : "Offline"} />
      <InfoRow label="Last sync" testId="sync-center-last-sync" value={formatClock(status.lastSyncAt)} />
      <InfoRow label="Local dispatch rows" testId="sync-center-row-count" value={status.ready ? status.boardRowCount : "Not downloaded yet"} />
      <InfoRow label="Pending changes" testId="sync-center-pending-count" value={status.pendingCount} />
    </div>

    <label className="flex items-center justify-between gap-3 text-sm" data-testid="sync-center-local-first-toggle">
      <span className="text-zinc-700">Read from local data (fast mode)</span>
      <input checked={status.localFirst} className="h-4 w-4 rounded border-zinc-300" data-testid="sync-center-local-first-checkbox" onChange={(event) => syncEngine.setLocalFirstEnabled(event.target.checked)} type="checkbox" />
    </label>

    {outbox.length ? (
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Waiting to sync</p>
        <ul className="max-h-48 space-y-2 overflow-y-auto" data-testid="sync-outbox-list">{outbox.map((item) => <OutboxItem item={item} key={item.id} />)}</ul>
      </div>
    ) : null}

    <div className="flex gap-2">
      <button className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-zinc-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-zinc-800" data-testid="sync-center-sync-now" onClick={() => syncEngine.syncNow()} type="button"><RefreshCw className="h-4 w-4" />Sync now</button>
      <button className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-zinc-200 px-3 py-2 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="sync-center-refresh-all" onClick={() => syncEngine.refreshAll()} type="button"><CloudOff className="h-4 w-4" />Re-download</button>
    </div>
  </>
);

export const SyncCenterPanel = ({ user }) => {
  const status = useSyncStatus();
  const outbox = useSyncOutbox();
  const isAdmin = user?.role === "Admin";
  const [tab, setTab] = useState("status");
  const tabClass = (active) => `flex-1 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${active ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`;
  return (
    <div className="panel-surface absolute right-0 top-full z-40 mt-2 w-[22rem] space-y-4 px-4 py-4 text-left shadow-xl" data-testid="sync-center-panel">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Sync Center</p>
        <p className="mt-1 text-sm font-semibold text-zinc-900" data-testid="sync-center-state">{STATE_LABELS[status.state] || status.state}</p>
        {status.lastError ? <p className="mt-1 text-xs text-red-600" data-testid="sync-center-error">{status.lastError}</p> : null}
      </div>

      {isAdmin ? (
        <div className="flex gap-1 rounded-2xl border border-zinc-200 bg-zinc-50 p-1" data-testid="sync-center-tabs">
          <button className={tabClass(tab === "status")} data-testid="sync-center-tab-status" onClick={() => setTab("status")} type="button">Status</button>
          <button className={tabClass(tab === "conflicts")} data-testid="sync-center-tab-conflicts" onClick={() => setTab("conflicts")} type="button">
            Conflicts{status.openConflicts ? <span className="ml-1.5 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] text-zinc-900" data-testid="sync-center-conflicts-count">{status.openConflicts}</span> : null}
          </button>
        </div>
      ) : null}

      {tab === "conflicts" && isAdmin ? <ConflictsTab isAdmin={isAdmin} /> : <StatusTab outbox={outbox} status={status} />}
    </div>
  );
};
